import { Loading3QuartersOutlined } from "@ant-design/icons";
import { Button, Col, Form, Row, Spin } from "antd";
import { useForm } from "react-hook-form";
import { InputField, InputPhone } from "../../../../atoms/FormFields";
import { useEffect, useState } from "react";
import UploadPhoto from "../../../../atoms/UploadPhoto";
import { UserType } from "../../ducks/types";
import {
  onImageDelete,
  updateUserById,
  uploadUserImage,
} from "../../ducks/services";
import { SuccessMessage } from "../../../../atoms/Toasts/SuccessMessage";
import { ErrorMessage } from "../../../../atoms/Toasts/ErrorMessage";

import { useAppDispatch } from "../../../../config/hooks";
import { getUserDetails } from "../../ducks/actions";
import { AxiosError, AxiosResponse } from "axios";
import { RcFile } from "antd/es/upload";

const antIcon = <Loading3QuartersOutlined spin />;

interface PropsType {
  onClose: () => void;
  data: UserType | null;
  setIsCropModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

interface FormValues {
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
}

export default function MyProfilePopUp(props: PropsType) {
  const { onClose, data, setIsCropModalOpen } = props;
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const {
    control,
    setValue,
    formState: { errors },
    handleSubmit,
  } = useForm<FormValues>();

  function callMembersAPI() {
    dispatch(getUserDetails());
  }

  const onSubmit = (val: FormValues) => {
    setLoading(true);
    const payload = {
      first_name: val?.first_name,
      last_name: val?.last_name,
      email: val?.email,
      phone: val?.phone ?? "",
      type: data?.type,
    };

    if (data) {
      updateUserById(payload, data.id)
        .then((res: AxiosResponse) => {
          if (checklistFile && res && res?.status >= 200 && res?.status < 300) {
            uploadUserImage({
              owner_id: data.id,
              image: checklistFile as File | RcFile,
              image_type: "user_image",
            })
              .then((res) => {
                if (res) {
                  setLoading(false);
                  dispatch(getUserDetails());
                  SuccessMessage("Profile updated");
                  onClose();
                }
              })
              .catch((e: unknown) => {
                if (e instanceof AxiosError) {
                  setLoading(false);
                  ErrorMessage(
                    e?.response?.data?.error?.message ?? "Something went wrong"
                  );
                } else {
                  setLoading(false);
                  ErrorMessage("Something went wrong");
                }
              });
          } else {
            setLoading(false);
            dispatch(getUserDetails());
            SuccessMessage("Profile updated");
            onClose();
          }
        })
        .catch((e: unknown) => {
          if (e instanceof AxiosError) {
            setLoading(false);
            ErrorMessage(
              e?.response?.data?.error?.message ?? "Something went wrong"
            );
          } else {
            setLoading(false);
            ErrorMessage("Something went wrong");
          }
        });
    }
  };

  useEffect(() => {
    if (data) {
      setValue("first_name", data.first_name ?? "");
      setValue("last_name", data.last_name ?? "");
      setValue("phone", data.phone ?? "");
      setValue("email", data.email ?? "");
    }
  }, [data, setValue]);

  const [checklistFile, setChecklistFile] = useState<File | null>(null);
  const [uploadModal, setUploadModal] = useState<boolean>(false);
  const [previewImage, setPreviewImage] = useState<{
    file: string;
    imagePreviewUrl: string | null;
  }>({ file: "", imagePreviewUrl: null });
  const [modalPreviewImage, setModalPreviewImage] = useState<{
    file: string;
    imagePreviewUrl: string | null;
  }>({ file: "", imagePreviewUrl: null });

  const saveUpload = () => {
    setLoading(true);
    setPreviewImage({ ...modalPreviewImage });
    setLoading(false);
    setUploadModal(false);
  };

  return (
    <Spin indicator={antIcon} size="large" spinning={loading}>
      <Form
        layout="vertical"
        onFinish={handleSubmit(onSubmit)}
        autoComplete="off"
        name="dependencies"
      >
        <div className="modalWrapper">
          <div className="modal_header">
            <div className="title">
              <h2>Update profile details</h2>
              <p>
                Your work profile information is visible to your colleagues.
              </p>
            </div>
          </div>

          <div className="modal_content">
            <Row gutter={16}>
              <Col span={24}>
                <UploadPhoto
                  saveUpload={saveUpload}
                  loading={loading}
                  uploadModal={uploadModal}
                  setUploadModal={setUploadModal}
                  previewImage={previewImage}
                  checklistFile={checklistFile}
                  modalPreviewImage={modalPreviewImage}
                  setChecklistFile={setChecklistFile}
                  setPreviewImage={setPreviewImage}
                  setModalPreviewImage={setModalPreviewImage}
                  setIsCropModalOpen={setIsCropModalOpen}
                  imageUploaded={data?.image_url}
                  onImageDelete={() => {
                    onImageDelete(
                      setLoading,
                      SuccessMessage,
                      callMembersAPI,
                      ErrorMessage,
                      data?.image_url ? data?.image_id : undefined
                    );
                  }}
                />
              </Col>
              <Col span={12}>
                <InputField
                  fieldName="first_name"
                  label="First name"
                  control={control}
                  iProps={{
                    placeholder: "Enter first name",
                  }}
                  isRequired
                  rules={{
                    required: "First name is required.",
                  }}
                  validate={errors.first_name && "error"}
                  validMessage={errors.first_name && errors.first_name.message}
                />
              </Col>

              <Col span={12}>
                <InputField
                  fieldName="last_name"
                  label="Last name"
                  control={control}
                  iProps={{
                    placeholder: "Enter last name",
                  }}
                  isRequired
                  rules={{
                    required: "Last name is required.",
                  }}
                  validate={errors.last_name && "error"}
                  validMessage={errors.last_name && errors.last_name.message}
                />
              </Col>
              <Col span={12} className="mb-0">
                <InputPhone
                  fieldName="phone"
                  label="Mobile Phone"
                  control={control}
                />
              </Col>
              <Col span={12} className="mb-0">
                <InputField
                  fieldName="email"
                  label="Account email"
                  control={control}
                  iProps={{
                    placeholder: "Enter email",
                  }}
                  isRequired
                  rules={{
                    required: "Account email is required.",
                    pattern: {
                      value:
                        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                      message: "Please enter a valid email address.",
                    },
                  }}
                  validate={errors.email && "error"}
                  validMessage={errors.email && errors.email.message}
                />
              </Col>
            </Row>
          </div>

          <div className="modal_footer" style={{ marginLeft: "auto" }}>
            <Button
              htmlType="button"
              type="default"
              size="large"
              className="outlined-btn"
              onClick={onClose}
            >
              Cancel
            </Button>

            <Button
              htmlType="submit"
              type="primary"
              size="large"
              className="mint-btn"
              // disabled={!isDirty || !isValid}
            >
              Save changes
            </Button>
          </div>
        </div>
      </Form>
    </Spin>
  );
}
